// Mirrors backend com.kervolt.oms.financing.borrowing.dto.BorrowingDtos (W3-A).

export type FacilityType = 'TERM_LOAN' | 'TIME_LOAN' | 'PACKING_CREDIT' | 'EDF' | 'LTR' | 'PAD' | 'OVERDRAFT' | 'DIRECTOR_LOAN';
export type RateType = 'FIXED' | 'FLOATING';
export type DayCount = 'ACT_365' | 'ACT_360';
export type RepaymentType = 'BULLET' | 'EQUAL_PRINCIPAL' | 'ANNUITY';
export type ScheduleFrequency = 'MONTHLY' | 'QUARTERLY';
export type CovenantMetric = 'CURRENT_RATIO' | 'DEBT_EQUITY' | 'DSCR' | 'MIN_NET_WORTH';
export type CovenantComparator = 'GTE' | 'LTE';

export const FACILITY_TYPES: FacilityType[] = ['TERM_LOAN', 'TIME_LOAN', 'PACKING_CREDIT', 'EDF', 'LTR', 'PAD', 'OVERDRAFT', 'DIRECTOR_LOAN'];
export const REPAYMENT_TYPES: RepaymentType[] = ['BULLET', 'EQUAL_PRINCIPAL', 'ANNUITY'];
export const FREQUENCIES: ScheduleFrequency[] = ['MONTHLY', 'QUARTERLY'];
export const COVENANT_METRICS: CovenantMetric[] = ['CURRENT_RATIO', 'DEBT_EQUITY', 'DSCR', 'MIN_NET_WORTH'];

export interface RateChange { id: string; effectiveDate: string; ratePct: number; note?: string | null }

export interface Facility {
  id: string; institutionId: string; facilityNo: string; name: string; facilityType: FacilityType;
  lenderName: string; bankAccountId?: string | null; currencyCode?: string | null; sanctionedLimit: number;
  validFrom?: string | null; validTo?: string | null; rateType?: RateType | null; spreadPct?: number | null;
  dayCount?: DayCount | null; principalAccountId?: string | null; interestExpenseAccountId?: string | null;
  interestPayableAccountId?: string | null; chargesAccountId?: string | null; segmentId?: string | null;
  status: string; remarks?: string | null; currentRatePct?: number | null; rates: RateChange[];
  /** W3-B: packing credit only: % of the export LC base that may be drawn (null = no check). */
  pcEligibilityPct?: number | null;
}

export interface FacilityRequest {
  institutionId: string; facilityNo: string; name: string; facilityType: FacilityType; lenderName: string;
  bankAccountId?: string | null; currencyCode?: string | null; sanctionedLimit: number;
  validFrom?: string | null; validTo?: string | null; rateType?: RateType; spreadPct?: number | null;
  dayCount?: DayCount; principalAccountId?: string | null; interestExpenseAccountId?: string | null;
  interestPayableAccountId?: string | null; chargesAccountId?: string | null; segmentId?: string | null;
  remarks?: string | null; initialRatePct?: number | null; rateEffectiveFrom?: string | null;
  pcEligibilityPct?: number | null;
}

export interface ScheduleLine {
  id: string; seq: number; dueDate: string; principalDue: number; interestDue: number; principalPaid: number; status: string;
}

export interface Drawdown {
  id: string; facilityId: string; drawdownNo: string; drawdownDate: string; amount: number;
  foreignAmount?: number | null; exchangeRate?: number | null; maturityDate?: string | null;
  repaymentType: RepaymentType; frequency?: ScheduleFrequency | null; graceMonths: number; installments: number;
  bankAccountId?: string | null; opening: boolean; status: string; journalEntryId?: string | null;
  reference?: string | null; remarks?: string | null; outstanding: number; schedule: ScheduleLine[];
  exportLcId?: string | null; b2bAcceptanceId?: string | null; origin?: 'CASH' | 'B2B_RETIREMENT' | string | null;
}

export interface DrawdownRequest {
  facilityId: string; drawdownNo: string; drawdownDate: string; amount?: number | null;
  foreignAmount?: number | null; exchangeRate?: number | null; maturityDate?: string | null;
  repaymentType: RepaymentType; frequency?: ScheduleFrequency | null; graceMonths?: number | null;
  installments?: number | null; bankAccountId?: string | null; opening?: boolean; reference?: string | null; remarks?: string | null;
  /** W3-B: required for a packing-credit facility, refused otherwise. */
  exportLcId?: string | null;
}

export interface Repayment {
  id: string; facilityId: string; drawdownId: string; paymentDate: string; totalAmount: number;
  interestAmount: number; principalAmount: number; prepaymentAmount: number; chargesAmount: number;
  bankAccountId?: string | null; reference?: string | null; remarks?: string | null; status: string; journalEntryId?: string | null;
  /** W3-B: BANK, or EXPORT_REALIZATION for a loan liquidated from export proceeds. */
  source?: string | null; sourceId?: string | null;
  foreignPrincipalAmount?: number | null; exchangeRate?: number | null; fxGainLossBase?: number | null;
}

export interface RepaymentRequest {
  drawdownId: string; paymentDate: string; totalAmount?: number | null; chargesAmount?: number | null;
  interestAmount?: number | null; bankAccountId: string; allowPrepayment?: boolean; reference?: string | null; remarks?: string | null;
  /** W3-B: a foreign-currency drawdown is repaid with the foreign principal and the rate the bank charged. */
  foreignPrincipalAmount?: number | null; exchangeRate?: number | null;
}

export interface RepaymentPreview {
  totalAmount: number; chargesAmount: number; interestAmount: number; principalDueAmount: number;
  prepaymentAmount: number; accruedInterestUnpaid: number; outstandingBefore: number; outstandingAfter: number;
}

export interface InterestLine {
  id: string; runId: string; facilityId: string; facilityNo: string; facilityName: string; period: string;
  days: number; interestAmount: number; status: string; journalEntryId?: string | null; error?: string | null;
}

export interface InterestRun {
  id?: string | null; period: string; status: string; triggerSource: string; runAt?: string | null;
  totalInterest: number; lines: InterestLine[];
}

export interface UtilisationRow {
  facilityId: string; facilityNo: string; name: string; facilityType: FacilityType; lenderName: string;
  currencyCode?: string | null; sanctionedLimit: number; outstanding: number; available: number;
  utilisationPct?: number | null; validTo?: string | null; daysToExpiry?: number | null; expiringSoon: boolean;
  accruedInterestUnpaid: number; currentRatePct?: number | null;
}
export interface UtilisationReport { institutionId: string; asOf: string; rows: UtilisationRow[]; totalLimit: number; totalOutstanding: number }

export interface OdPositionRow {
  bankAccountId?: string | null; facilityId?: string | null; facilityNo?: string | null; bankName?: string | null;
  limit: number; glBalance: number; utilised: number; headroom: number; overLimit: boolean;
}
export interface OdPositionReport { institutionId: string; asOf: string; rows: OdPositionRow[] }

export interface LadderBucket { key: string; label: string; amount: number }
export interface FacilityLadderRow {
  facilityId: string; facilityNo: string; name: string; facilityType: FacilityType; currencyCode?: string | null;
  outstanding: number; current: number; nonCurrent: number; buckets: Record<string, number>;
}
export interface MaturityLadderReport {
  institutionId: string; asOf: string; buckets: LadderBucket[]; current: number; nonCurrent: number;
  totalOutstanding: number; facilities: FacilityLadderRow[];
}

export interface TieOutRow {
  kind: string; accountId?: string | null; accountCode?: string | null; accountName?: string | null;
  registerAmount: number; glAmount: number; difference: number;
}
export interface TieOutReport { institutionId: string; asOf: string; rows: TieOutRow[]; tiesOut: boolean }

export interface Covenant {
  id: string; facilityId?: string | null; facilityNo?: string | null; metric: CovenantMetric;
  comparator: CovenantComparator; threshold: number; active: boolean; note?: string | null;
}
export interface CovenantRequest {
  institutionId: string; facilityId?: string | null; metric: CovenantMetric; comparator: CovenantComparator;
  threshold: number; active?: boolean; note?: string | null;
}
export interface CovenantResult {
  covenantId: string; facilityId?: string | null; facilityNo?: string | null; metric: CovenantMetric;
  comparator: CovenantComparator; threshold: number; actual?: number | null; result: 'PASS' | 'BREACH' | 'N/A'; detail?: string | null;
}
export interface CovenantReport { institutionId: string; asOf: string; results: CovenantResult[] }

export interface BorrowingDueItem {
  sourceKey: string; documentId: string; documentRef: string; dueDate: string; amount: number;
  currencyCode?: string | null; title: string; deepLink?: string | null; severity: string;
}
